// frontend/src/hooks/useAdoptPrehydrationValue.js
//
// Keep what a visitor typed into a prerendered snapshot's field before React
// attached to it - backlog #464. The sibling of useHydrationSafeAutoFocus.js.
//
// THE PROBLEM. A prerendered page (/login, /register, /forgot-password) is on
// screen, as real inputs, before its JavaScript arrives - so a visitor can type
// into a field, or the browser can autofill it, while React is not there yet.
// React does not overwrite an input's value when it HYDRATES it, but the
// component's state still holds the empty value the snapshot was taken with,
// and the next render of that controlled input - the post-hydration render
// useHydrated() triggers, at the latest - writes the empty state value back
// into the DOM. The visitor's text vanishes under their fingers.
//
// THE RULE. On the hydration pass of a snapshot only, and only once: when the
// state value is empty and the input holds text, hand that text to `adopt` -
// which puts it into state exactly as the field's onChange would have. From a
// layout effect, so the adopted value is in state before the browser paints
// the post-hydration render. Never on a client-only boot (a route change, the
// dev server): there `hydrated` is true from the first render and there is no
// snapshot whose text could be lost.
//
// What it never does: log, measure or otherwise look at the value beyond
// "is it empty" - the input may be a password.
//
// NOT for a consent checkbox. A tick that reached the snapshot before React -
// including one the browser's form restoration put back on its own - is not
// consent; see useDiscardPrehydrationCheck.js.

import { useLayoutEffect } from "react";
import useHydrated from "../motion/useHydrated";

/**
 * @param {{current: (HTMLInputElement|null)}} ref - the field's input element
 *   (an MUI TextField's `inputRef`). A ref still null at hydration - the field
 *   is not on screen - is simply skipped.
 * @param {string|null|undefined} stateValue - the value the field rendered
 *   with on this render. Anything non-empty means state already holds the
 *   visitor's text (or a seed) and nothing is adopted.
 * @param {(domValue: string) => void} adopt - puts the input's text into state.
 */
export default function useAdoptPrehydrationValue(ref, stateValue, adopt) {
  const hydrated = useHydrated();

  useLayoutEffect(() => {
    // `hydrated` is false only in the commit of the hydration pass - and stays
    // true for good from the next render on - so this adopts at most once.
    if (hydrated || stateValue) return;
    const el = ref.current;
    const domValue = el && typeof el.value === "string" ? el.value : "";
    if (domValue) adopt(domValue);
  }, [hydrated, stateValue, ref, adopt]);
}

export { useAdoptPrehydrationValue };
